import React, { useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Autocomplete,
  TextField,
  CircularProgress,
  Paper,
} from "@mui/material";
import { Button, DatePicker } from "antd";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  Legend,
} from "recharts";
import { useAuth } from "../../context/AuthProvider";
import { encryptData } from "../../utils/cryptoUtils";
import {
  getActivitySummary,
  getActivityMasterList,
} from "../../services/timeTracker.service";
import { isAdminRole } from "../../utils/roles";

const COLORS = [
  "#14b8a6", "#6366f1", "#f59e0b", "#ef4444", "#8b5cf6",
  "#10b981", "#3b82f6", "#ec4899", "#f97316", "#64748b",
];

// minutes → "Hh Mm" / "Mm"
function fmt(min) {
  const m = Math.max(0, Math.round(min || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h > 0 ? `${h}h ${r}m` : `${r}m`;
}

const ellipsis = {
  maxWidth: 320,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const AppUsageTracking = () => {
  const { accessToken } = useAuth();

  const [isAdmin, setIsAdmin] = useState(false);
  const [ownUserId, setOwnUserId] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  // Chart shows only the top 5 by default; "View all" expands it to every app + site.
  const [showAllChart, setShowAllChart] = useState(false);

  const toggle = (name) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });

  useEffect(() => {
    if (!accessToken) return;
    try {
      const d = jwtDecode(accessToken);
      const admin = isAdminRole(d?.role);
      setIsAdmin(admin);
      setOwnUserId(d?.id || null);
      if (!admin) setSelectedUser({ userId: d?.id, userName: "Me" });
    } catch (e) {
      console.error("token decode failed", e);
    }
  }, [accessToken]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await getActivityMasterList(encryptData(dayjs().format("YYYY-MM-DD")));
        const list = (res?.data || []).map((u) => ({
          userId: String(u.userId),
          userName: u.userName || u.email || String(u.userId),
        }));
        setUsers(list);
      } catch (e) {
        console.error("user list load failed", e);
      }
    })();
  }, [isAdmin]);

  const fetchData = useCallback(async () => {
    const userId = selectedUser?.userId || ownUserId;
    if (!userId || !selectedDate) return;
    setLoading(true);
    setExpanded(new Set());
    try {
      const fromISO = selectedDate.startOf("day").toISOString();
      const toISO = selectedDate.endOf("day").toISOString();
      const res = await getActivitySummary(
        encryptData(fromISO),
        encryptData(toISO),
        encryptData(userId)
      );
      setSummary(res?.data || null);
    } catch (e) {
      console.error("app usage fetch failed", e);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [selectedUser, ownUserId, selectedDate]);

  useEffect(() => {
    if (selectedUser?.userId || ownUserId) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, ownUserId]);

  // Keep idle markers ("Idle (kept)" / "Away") — expanding them shows the kept /
  // discarded time ranges. Only hide truly undetected apps ("unknown").
  const topApps = (summary?.topApps || []).filter((a) => a.app && a.app !== "unknown");
  const topSites = summary?.topSites || [];

  // Idle markers tracked as "apps": kept idle ("Idle (kept)") and discarded idle ("Away").
  // They live in sampleCount too, so we account for them separately to keep the cards honest.
  const IDLE_MARKERS = new Set(["Idle (kept)", "Away"]);

  // Total Tracked is the authoritative count of distinct tracked minutes.
  const totalMinutes = summary?.sampleCount || 0;
  // Total Idle = minutes the user was idle (kept as work or discarded).
  const idleMinutes = topApps
    .filter((a) => IDLE_MARKERS.has(a.app))
    .reduce((s, a) => s + (a.minutes || 0), 0);

  // Every app + website ranked by time. Default chart shows the top 8; "View all"
  // shows the whole list.
  const allPieData = [
    ...topApps.map((a) => ({ name: a.app, value: a.minutes })),
    ...topSites.map((s) => ({ name: s.domain, value: s.minutes })),
  ]
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);
  const pieData = showAllChart ? allPieData : allPieData.slice(0, 5);

  // Build the drill-down rows for an app: idle ranges for idle markers, URLs for a
  // browser, else file/page titles.
  const detailOf = (a) => {
    if (a.ranges && a.ranges.length) {
      return a.ranges.map((r, i) => {
        const span = `${dayjs(r.startedAt).format("HH:mm")} – ${dayjs(r.endedAt).format("HH:mm")}`;
        return {
          key: `r-${i}`,
          primary: r.reason ? `${span}  ·  ${r.reason}` : span,
          secondary: r.reason ? `${a.app} · ${r.reason}` : a.app,
          minutes: r.minutes,
        };
      });
    }
    if (a.sites && a.sites.length) {
      return a.sites.map((s, i) => ({
        key: `s-${s.domain}-${i}`,
        primary: s.domain,
        secondary: s.url || s.domain,
        minutes: s.minutes,
      }));
    }
    return (a.sampleTitles || []).map((t, i) => ({
      key: `t-${i}`,
      primary: typeof t === "string" ? t : t.title,
      secondary: typeof t === "string" ? t : t.title,
      minutes: typeof t === "string" ? null : t.minutes,
    }));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        App &amp; Site Usage
      </Typography>

      {/* Filters */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
        {isAdmin && (
          <Autocomplete
            size="small"
            sx={{ minWidth: 240 }}
            options={users}
            getOptionLabel={(o) => o.userName || ""}
            isOptionEqualToValue={(o, v) => o.userId === v.userId}
            value={selectedUser}
            onChange={(_e, v) => setSelectedUser(v)}
            renderInput={(params) => <TextField {...params} label="Employee" />}
          />
        )}
        <DatePicker value={selectedDate} onChange={(v) => v && setSelectedDate(v)} allowClear={false} />
        <Button type="primary" onClick={fetchData} loading={loading}>
          Search
        </Button>
      </Box>

      {/* Summary cards */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <SummaryCard label="Total Tracked" value={fmt(totalMinutes)} />
        <SummaryCard label="Total Idle" value={fmt(idleMinutes)} />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {/* Top Apps — click a row to drill into its URLs (browser) or files/pages */}
          <Card sx={{ flex: "1 1 520px", minWidth: 360 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Top Apps
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click an app to see the URLs / files used inside it
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 480, mt: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>App</TableCell>
                      <TableCell align="right">Active</TableCell>
                      <TableCell align="right">Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topApps.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ color: "text.secondary" }}>
                          No app data for this range
                        </TableCell>
                      </TableRow>
                    )}
                    {topApps.map((a) => {
                      const isOpen = expanded.has(a.app);
                      const detail = isOpen ? detailOf(a) : [];
                      return (
                        <React.Fragment key={a.app}>
                          <TableRow hover sx={{ cursor: "pointer" }} onClick={() => toggle(a.app)}>
                            <TableCell sx={{ fontWeight: 600 }}>
                              <span style={{ display: "inline-block", width: 16 }}>{isOpen ? "▾" : "▸"}</span>
                              {a.app}
                            </TableCell>
                            <TableCell align="right" sx={{ color: "#4c8dff" }}>
                              {a.avgActivity ?? 0}%
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(a.minutes)}</TableCell>
                          </TableRow>
                          {isOpen && detail.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={3} sx={{ pl: 5, color: "text.secondary", fontSize: 12 }}>
                                No detail recorded
                              </TableCell>
                            </TableRow>
                          )}
                          {isOpen &&
                            detail.map((d) => (
                              <TableRow key={d.key} sx={{ bgcolor: "#f8fafc" }}>
                                <TableCell sx={{ pl: 5, ...ellipsis, color: "#475569" }}>
                                  <Tooltip title={d.secondary} placement="top" arrow>
                                    <span>{d.primary}</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell />
                                <TableCell align="right" sx={{ color: "#64748b", fontSize: 12 }}>
                                  {d.minutes != null ? fmt(d.minutes) : ""}
                                </TableCell>
                              </TableRow>
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Donut */}
          <Card sx={{ flex: "1 1 360px", minWidth: 300 }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Top Apps and Websites Chart
                </Typography>
                {allPieData.length > 5 && (
                  <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setShowAllChart((v) => !v)}>
                    {showAllChart ? `Show top 5` : `View all (${allPieData.length})`}
                  </Button>
                )}
              </Box>
              {pieData.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>No data</Box>
              ) : (
                <ResponsiveContainer width="100%" height={showAllChart ? 460 : 340}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={65} outerRadius={115} paddingAngle={2}>
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <ReTooltip formatter={(v, n) => [fmt(v), n]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Top Websites (flat) */}
          <Card sx={{ flex: "1 1 100%", minWidth: 320 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Top Websites
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Website</TableCell>
                      <TableCell align="right">Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topSites.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ color: "text.secondary" }}>
                          No website data — browsers weren't used, or URL capture is off on this OS
                        </TableCell>
                      </TableRow>
                    )}
                    {topSites.map((s, i) => (
                      <TableRow key={`${s.domain}-${i}`} hover>
                        <TableCell sx={ellipsis}>
                          <Tooltip title={s.url || s.domain} placement="top" arrow>
                            <span>{s.domain}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">{fmt(s.minutes)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

function SummaryCard({ label, value }) {
  return (
    <Card sx={{ flex: "1 1 160px", minWidth: 150 }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

export default AppUsageTracking;
